// Ship the overlay families to the browser so the client canvas (whose pixels
// are what actually gets published) renders the SAME faces the server does.
// Self-hosted @fontsource, mirroring the worker image's vendored set
// (collector fonts/ + Dockerfile): weights 400/600/700/800 where the family
// has them, plus the italics the server actually carries. Families with fewer
// faces (Bebas Neue/Anton = 400 only; Oswald no italic; Lato 400/700/900;
// Roboto 400/500/700/900) import what exists — CSS closest-weight matching
// covers the rest, exactly like fontconfig does on the server.
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/800.css';
import '@fontsource/montserrat/400-italic.css';
import '@fontsource/montserrat/600-italic.css';
import '@fontsource/montserrat/700-italic.css';
import '@fontsource/montserrat/800-italic.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';
import '@fontsource/poppins/400-italic.css';
import '@fontsource/poppins/700-italic.css';
import '@fontsource/oswald/400.css';
import '@fontsource/oswald/600.css';
import '@fontsource/oswald/700.css';
import '@fontsource/bebas-neue/400.css';
import '@fontsource/anton/400.css';
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/600.css';
import '@fontsource/playfair-display/700.css';
import '@fontsource/playfair-display/800.css';
import '@fontsource/playfair-display/400-italic.css';
import '@fontsource/playfair-display/600-italic.css';
import '@fontsource/playfair-display/700-italic.css';
import '@fontsource/playfair-display/800-italic.css';
import '@fontsource/inter/800.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto/900.css';
import '@fontsource/roboto/400-italic.css';
import '@fontsource/roboto/700-italic.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';
import '@fontsource/open-sans/800.css';
import '@fontsource/open-sans/400-italic.css';
import '@fontsource/open-sans/600-italic.css';
import '@fontsource/open-sans/700-italic.css';
import '@fontsource/open-sans/800-italic.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import '@fontsource/lato/900.css';
import '@fontsource/lato/400-italic.css';
import '@fontsource/lato/700-italic.css';
import '@fontsource/lato/900-italic.css';
import '@fontsource/roboto-slab/400.css';
import '@fontsource/roboto-slab/600.css';
import '@fontsource/roboto-slab/700.css';
import '@fontsource/roboto-slab/800.css';
import '@fontsource/dejavu-sans/400.css';
import '@fontsource/dejavu-sans/700.css';
import '@fontsource/dejavu-sans/400-italic.css';
import '@fontsource/dejavu-sans/700-italic.css';

import { parseInlineMarkup } from './inlineMarkup';
import { OverlayElement } from './types';

// The server's whitelist (textOverlay.js FONT_FAMILIES): a family outside it
// (and outside the user's custom fonts) falls back to DejaVu Sans there, so
// the client must resolve the same name.
export const SERVER_FONT_FAMILIES = [
  'Montserrat',
  'Poppins',
  'Oswald',
  'Bebas Neue',
  'Anton',
  'Playfair Display',
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Roboto Slab',
  'DejaVu Sans',
  'Liberation Sans',
  'Liberation Sans Narrow',
  'DejaVu Serif',
  'Liberation Serif',
  'DejaVu Sans Mono',
];
export const DEFAULT_OVERLAY_FONT = 'DejaVu Sans';

// The subset actually shipped above as webfonts (the Liberation/extra DejaVu
// faces have no @fontsource package — they resolve only where the OS has
// them, which loadOverlayFonts detects).
const WEBFONT_FAMILIES = [
  'Montserrat', 'Poppins', 'Oswald', 'Bebas Neue', 'Anton', 'Playfair Display',
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Roboto Slab', 'DejaVu Sans',
];

function resolveFamily(requested: string | undefined, customFamilies: string[]): string {
  if (requested && SERVER_FONT_FAMILIES.includes(requested)) return requested;
  if (requested && customFamilies.includes(requested)) return requested;
  return DEFAULT_OVERLAY_FONT;
}

const boldBump = (weight: number) => Math.min(900, Math.max(700, weight + 100));

/**
 * Every `{style} {weight} 16px '{family}'` shorthand the spec's rendering
 * touches: the base face per element, the bold-emphasis bump fontString()
 * applies to **bold** segments, and italics from inline markup or the
 * element's own style. Pure, for tests.
 */
export function fontShorthands(elements: OverlayElement[], customFamilies: string[] = []): string[] {
  const shorthands = new Set<string>();
  for (const el of elements) {
    const family = resolveFamily(el.fontFamily, customFamilies);
    const baseWeight = el.fontWeight || 400;
    const lines = (el.lines || []).map(String).filter(l => l.length);
    if (!lines.length) continue;
    for (const line of lines) {
      for (const seg of parseInlineMarkup(line)) {
        const weight = seg.bold ? boldBump(baseWeight) : baseWeight;
        const style = seg.italic || el.fontStyle === 'italic' ? 'italic' : 'normal';
        shorthands.add(`${style} ${weight} 16px '${family}'`);
      }
    }
  }
  return [...shorthands];
}

const LOAD_TIMEOUT_MS = 5000;

async function faceResolves(shorthand: string): Promise<boolean> {
  const fonts = (document as any).fonts;
  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('font load timed out')), LOAD_TIMEOUT_MS);
  });
  const faces = await Promise.race([fonts.load(shorthand), timeout]);
  if (Array.isArray(faces) && faces.length) {
    return faces.every((f: FontFace) => f.status === 'loaded');
  }
  // No registered @font-face matched — a system-installed family (Liberation
  // on Linux, DejaVu where present) still satisfies check().
  return Boolean(fonts.check?.(shorthand));
}

/**
 * Load every face the spec needs; true only when ALL resolved — the caller's
 * gate between the client canvas render and the server fallback (which has
 * every family guaranteed).
 */
export async function loadOverlayFonts(elements: OverlayElement[], customFamilies: string[] = []): Promise<boolean> {
  const fonts = (document as any).fonts;
  if (!fonts?.load) return false;
  const results = await Promise.allSettled(fontShorthands(elements, customFamilies).map(faceResolves));
  return results.every(r => r.status === 'fulfilled' && r.value === true);
}

/** Non-blocking warm of every shipped family, so the font dropdown previews
 * real faces instead of fallbacks on first open. */
export function loadAllOverlayFonts(): void {
  const fonts = (document as any).fonts;
  if (!fonts?.load) return;
  for (const family of WEBFONT_FAMILIES) {
    void fonts.load(`400 16px '${family}'`).catch(() => {});
    void fonts.load(`700 16px '${family}'`).catch(() => {});
  }
}
